import Box from '@mui/material/Box';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import NoData from 'components/Common/NoData';
import PermissionService from 'components/Common/PermissionService';
import React, { useContext } from 'react';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { CLEAN_ROOM_STATUS, cleanRoomQuestionPermissions as crqPerm } from 'components/CleanRooms/constants';
import { FALLBACK_QUESTION_VERSION } from 'utils/constants/question.constant';
import {
  HButton, HDialog, HDialogActions, HDialogContent, HDialogTitle,
} from 'BaseComponents';
import { IdContext } from 'pages/CleanRooms/QuestionDetails';
import { cleanRoomPermissions } from 'utils/appConstants';
import { copyToClipboard } from 'utils/jsUtils';
import { crqAnalysisRuleStatus } from 'utils/ignoramusEnums';
import { deduplicateFailures, getFailureMessage, getFailureTitle } from './analysisRuleRunErrors';
import { habuColors } from 'Theme';
import { hasCRQuestionPermission } from 'components/CleanRooms/service';
import { question as questionURL } from 'utils/spaUrls';
import { showAlert } from 'redux/actions/AlertActions';
import { useDispatch } from 'react-redux';
import { useGetAnalysisRuleRunStatus } from 'api/datasetAnalysisRule';
import { useHistory } from 'react-router-dom';
import { v4 as uuidGen } from 'uuid';


const LoadingSkeleton = () => Array.from({ length: 6 },
  (_, i) => <Skeleton variant='rectangular' key={i} height={24} sx={{ mb: 2 }} />);

const AnalysisRuleRunStatusDialog = ({
  open,
  onClose,
  crId,
  question,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { orgId, crDetail } = useContext(IdContext);

  const {
    isLoading,
    isError,
    isSuccess,
    data: { analysisRuleRunStatus = [] } = {},
  } = useGetAnalysisRuleRunStatus(crId, question?.ID);

  const failedRules = analysisRuleRunStatus.filter(
    (runStatus) => runStatus.status === crqAnalysisRuleStatus.FAILED.key,
  );

  // Check permissions - same logic as Edit action in menu (CurrentQuestionsPageContextProvider)
  const isAuthorizedQB = PermissionService.isAuthorized([
    cleanRoomPermissions.questionBuilder,
  ]);

  const cleanRoomExpired = crDetail?.status === CLEAN_ROOM_STATUS.EXPIRED.key;

  const hasCRQPermEdit = question ? hasCRQuestionPermission(
    orgId,
    question.ownerOrganizationID,
    question.permissions,
    crqPerm.EDIT.name,
  ) : false;

  // Only show button when ALL conditions are met
  const showEditButton = isAuthorizedQB
    && question?.isEditable
    && !cleanRoomExpired
    && hasCRQPermEdit;

  const handleCopy = async (message) => {
    const success = await copyToClipboard(message);
    if (success) {
      dispatch(showAlert({ message: 'Copied to clipboard', type: 'success' }));
    }
    else {
      dispatch(showAlert({ message: 'Failed to copy to clipboard', type: 'error' }));
    }
  };

  const handleOpenInEditor = () => {
    if (question) {
      history.push(questionURL.builder.wizard.cleanRoom(
        question.questionID,
        crId,
        question.ID,
        question.questionVersion || FALLBACK_QUESTION_VERSION,
      ));
    }
  };

  return (
    <HDialog
      fullWidth
      open={open}
      maxWidth='sm'
      submitText='Ok'
      onClose={onClose}
    >
      <HDialogTitle onClose={onClose}>
        <Typography variant='h4' noWrap maxWidth='calc(100% - 40px)'>
          Question Run Failed – Dataset Analysis Rules Not Met
        </Typography>
      </HDialogTitle>
      <HDialogContent>
        {isLoading && <LoadingSkeleton />}
        {isError && <NoData text='Unable to retrieve question run failed.' />}
        {isSuccess && (failedRules.length === 0 ? (
          <NoData />
        ) : (
          <>
            <Box mb={2}>
              <Typography variant='body2'>
                The question run failed because one or more dataset analysis rules were not met.
                Try changing the question or asking the owner to change the analysis rule.
              </Typography>
            </Box>
            {failedRules.map((runStatus, index) => (
              <Stack key={uuidGen()} gap={1}>
                <Stack direction='row' alignItems='center' spacing={2}>
                  <Box flex={1}>
                    <Typography variant='body2'>
                      Analysis Rule
                    </Typography>
                    <Typography variant='body2' fontWeight='600'>
                      {runStatus.analysisRuleName}
                    </Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography variant='body2'>
                      Dataset
                    </Typography>
                    <Typography variant='body2' fontWeight='600'>
                      {runStatus.datasetName}
                    </Typography>
                  </Box>
                </Stack>
                {runStatus.failureDetails?.length > 0 && (
                  <Stack gap={2}>
                    {deduplicateFailures(runStatus.failureDetails).map((failure) => (
                      <Box
                        key={uuidGen()}
                        sx={{
                          bgcolor: 'error.x-light',
                          border: `1px solid ${habuColors.signal.alert[30]}`,
                          borderRadius: 1,
                          p: 2,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box flex={1}>
                          <Typography variant='body2' fontWeight='700' mb={0.5}>
                            {getFailureTitle(failure)}
                          </Typography>
                          <Typography variant='body2'>
                            {getFailureMessage(failure)}
                          </Typography>
                        </Box>
                        <Tooltip title='Copy error message' placement='top'>
                          <IconButton
                            size='small'
                            onClick={() => handleCopy(getFailureMessage(failure))}
                            sx={{
                              color: habuColors.signal.alert[80],
                              ml: 1,
                              '&:hover': {
                                bgcolor: 'error.light',
                              },
                            }}
                          >
                            <ContentCopyIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Stack>
                )}
                {failedRules.length - 1 !== index && (
                  <Divider sx={{ mt: 1, mb: 2 }} />
                )}
              </Stack>
            ))}
          </>
        )
        )}
      </HDialogContent>
      <HDialogActions>
        {showEditButton && (
          <HButton variant='outlined' onClick={handleOpenInEditor}>
            Open Question in Editor
          </HButton>
        )}
        <HButton variant='contained' onClick={onClose}>OK</HButton>
      </HDialogActions>
    </HDialog>
  );
};

export default AnalysisRuleRunStatusDialog;
